import { useEffect, useMemo, useState } from 'react'
import { Loader2, Search } from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { HandleChip } from '@/components/users/handle-chip'
import { useMyProjects } from '@/features/projects/use-projects'
import { useBrowse } from '@/features/discovery/use-browse'
import type { BrowseProject, ProjectSummary, StepNode } from '@/api/types'

interface ProjectPickerProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** The Project we're inserting INTO (excluded from the picker — can't reference yourself). */
  currentProjectId: string
  /** Steps in the current project — used to exclude any Project already referenced anywhere. */
  currentProjectSteps: StepNode[]
  /** Called for own-project picks. The selected row is one of the user's own ProjectSummary rows. */
  onSelectOwn: (project: ProjectSummary) => void
  /**
   * Phase 6: called for cross-author picks (results from the FTS search tab). The selected row is a
   * BrowseProject (PUBLIC source from any author). The handler resolves it into the same insert
   * flow as own-project picks; backend enforces source-must-be-PUBLIC.
   */
  onSelectFromBrowse: (project: BrowseProject) => void
  pending?: boolean
}

/**
 * Walk the tree once collecting referenced_project_id values so the picker can hide Projects
 * already linked into the current one. Phase-1-bounded — does not look across other Projects'
 * trees, since the backend's transitive cycle detector handles that case server-side.
 */
const directlyReferencedProjectIds = (steps: StepNode[]): Set<string> => {
  const ids = new Set<string>()
  const visit = (s: StepNode) => {
    if (s.referencedProjectId) ids.add(s.referencedProjectId)
    for (const child of s.children) visit(child)
  }
  steps.forEach(visit)
  return ids
}

export const ProjectPicker = ({
  open,
  onOpenChange,
  currentProjectId,
  currentProjectSteps,
  onSelectOwn,
  onSelectFromBrowse,
  pending,
}: ProjectPickerProps) => {
  const referencedIds = useMemo(
    () => directlyReferencedProjectIds(currentProjectSteps),
    [currentProjectSteps]
  )

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Insert another Project</DialogTitle>
          <DialogDescription>
            Snapshot another Project&apos;s tree under this Step. Pick one of your own, or search
            for a public Project to remix. Already-referenced Projects are hidden.
          </DialogDescription>
        </DialogHeader>

        <Tabs defaultValue="mine">
          <TabsList>
            <TabsTrigger value="mine">My projects</TabsTrigger>
            <TabsTrigger value="search">Search</TabsTrigger>
          </TabsList>
          <TabsContent value="mine" className="mt-3">
            <MineTab
              currentProjectId={currentProjectId}
              referencedIds={referencedIds}
              onSelect={onSelectOwn}
              pending={pending}
            />
          </TabsContent>
          <TabsContent value="search" className="mt-3">
            <SearchTab
              currentProjectId={currentProjectId}
              referencedIds={referencedIds}
              onSelect={onSelectFromBrowse}
              pending={pending}
            />
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  )
}

const MineTab = ({
  currentProjectId,
  referencedIds,
  onSelect,
  pending,
}: {
  currentProjectId: string
  referencedIds: Set<string>
  onSelect: (project: ProjectSummary) => void
  pending?: boolean
}) => {
  const { data, isLoading, isError } = useMyProjects(0)

  const candidates: ProjectSummary[] = useMemo(() => {
    if (!data) return []
    return data.content.filter((p) => p.id !== currentProjectId && !referencedIds.has(p.id))
  }, [data, currentProjectId, referencedIds])

  if (isLoading) {
    return (
      <div className="flex items-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" />
        Loading your Projects…
      </div>
    )
  }
  if (isError) {
    return <p className="text-sm text-destructive">Could not load your Projects. Try again.</p>
  }
  if (candidates.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">
        No other Projects available. Create one first, or remove an existing reference here to free
        it up.
      </p>
    )
  }
  return (
    <ul className="max-h-80 space-y-1 overflow-y-auto">
      {candidates.map((p) => (
        <li
          key={p.id}
          className="flex items-start justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2"
        >
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium">{p.title}</p>
            {p.description && (
              <p className="truncate text-xs text-muted-foreground">{p.description}</p>
            )}
          </div>
          <Button size="sm" variant="secondary" disabled={pending} onClick={() => onSelect(p)}>
            Select
          </Button>
        </li>
      ))}
    </ul>
  )
}

const SearchTab = ({
  currentProjectId,
  referencedIds,
  onSelect,
  pending,
}: {
  currentProjectId: string
  referencedIds: Set<string>
  onSelect: (project: BrowseProject) => void
  pending?: boolean
}) => {
  const [draft, setDraft] = useState('')
  const [debounced, setDebounced] = useState('')
  useEffect(() => {
    const handle = setTimeout(() => setDebounced(draft.trim()), 300)
    return () => clearTimeout(handle)
  }, [draft])

  const enabled = debounced.length > 0
  const { data, isFetching, isError } = useBrowse({ q: debounced, sort: 'recent', size: 10 })

  const filtered = useMemo(() => {
    if (!data) return [] as BrowseProject[]
    return data.items.filter((p) => p.id !== currentProjectId && !referencedIds.has(p.id))
  }, [data, currentProjectId, referencedIds])

  return (
    <div className="space-y-3">
      <div className="relative">
        <Search
          className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
          aria-hidden
        />
        <Input
          type="search"
          placeholder="Search public projects to reference"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="pl-9"
          aria-label="Search public projects to reference"
        />
      </div>

      {!enabled && (
        <p className="text-sm text-muted-foreground">
          Type to search. Only PUBLIC Projects can be referenced from another author&apos;s work.
        </p>
      )}
      {enabled && isFetching && (
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Searching…
        </div>
      )}
      {enabled && isError && <p className="text-sm text-destructive">Search failed — try again.</p>}
      {enabled && !isFetching && filtered.length === 0 && (
        <p className="text-sm text-muted-foreground">No matches for &ldquo;{debounced}&rdquo;.</p>
      )}
      {enabled && filtered.length > 0 && (
        <ul className="max-h-72 space-y-1 overflow-y-auto">
          {filtered.map((p) => (
            <li
              key={p.id}
              className="flex items-start justify-between gap-3 rounded-md border bg-muted/30 px-3 py-2"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-medium">{p.title}</p>
                {p.descriptionExcerpt && (
                  <p className="line-clamp-2 text-xs text-muted-foreground">
                    {p.descriptionExcerpt}
                  </p>
                )}
                <p className="mt-1 text-xs text-muted-foreground">
                  By <HandleChip author={p.author} className="text-xs" /> · updated{' '}
                  {new Date(p.updatedAt).toLocaleDateString()}
                </p>
              </div>
              <Button size="sm" variant="secondary" disabled={pending} onClick={() => onSelect(p)}>
                Select
              </Button>
            </li>
          ))}
        </ul>
      )}
    </div>
  )
}
